import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Page,
  Layout,
  Card,
  Banner,
  Badge,
  Text,
  BlockStack,
  InlineStack,
  InlineGrid,
  DataTable,
  SkeletonBodyText,
  Select,
  Box,
  Icon,
  List,
  TextField,
  Button,
  Spinner,
} from '@shopify/polaris';
import { MagicIcon, LightbulbIcon, ChartVerticalFilledIcon } from '@shopify/polaris-icons';
import { useShopifyFetch } from '../hooks/useShopifyFetch';

/**
 * Ally — the AI affiliate marketing assistant for Shopify merchants.
 *
 * One page, two surfaces:
 *   • Chat (primary) — live two-way conversation. Each turn POSTs to the backend
 *     `/api/ally/chat` proxy, which forwards to the affilync-api internal AI
 *     endpoint over ServiceToken auth and returns `{ response, conversation_id }`.
 *     The conversation_id is threaded across turns so Ally keeps context.
 *   • Insights (panel below) — headline metrics, top products, and promotion
 *     recommendations read live from `/api/ally/insights` (brand-scoped).
 */

/** Preserve the App Bridge host/shop query string on internal navigation. */
const withSearch = (path: string) => `${path}${window.location.search}`;

interface InsightProduct {
  title: string;
  conversions: number;
  revenue: number;
}

interface Recommendation {
  title: string;
  conversions?: number;
  revenue?: number;
  reason: string;
}

interface AllyInsights {
  connected: boolean;
  period_days: number;
  metrics: {
    clicks: number;
    conversions: number;
    revenue: number;
    conversion_rate: number;
  };
  top_products: InsightProduct[];
  recommendations: {
    double_down: Recommendation[];
    needs_promotion: Recommendation[];
    note: string;
  };
  generated_at: string;
}

interface ChatResponse {
  response: string;
  conversation_id: string | null;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'ally';
  text: string;
}

const PROMPT_SUGGESTIONS = [
  'What should I promote more?',
  'Which products underperform?',
  'How can I recruit more affiliates?',
  'Summarize my affiliate performance this month.',
];

const FRIENDLY_ERROR =
  'Ally is having trouble responding right now. Please try again in a moment.';

let messageCounter = 0;
function nextId(): string {
  messageCounter += 1;
  return `msg-${Date.now()}-${messageCounter}`;
}

const MetricCard = ({
  label,
  value,
  loading,
}: {
  label: string;
  value: string;
  loading: boolean;
}) => (
  <Card>
    <BlockStack gap="200">
      <Text as="span" variant="bodySm" tone="subdued">
        {label}
      </Text>
      {loading ? (
        <SkeletonBodyText lines={1} />
      ) : (
        <Text as="p" variant="heading2xl">
          {value}
        </Text>
      )}
    </BlockStack>
  </Card>
);

function MessageBubble({ message }: { message: ChatMessage }) {
  const isUser = message.role === 'user';
  return (
    <InlineStack align={isUser ? 'end' : 'start'}>
      <Box maxWidth="80%">
        <Card background={isUser ? 'bg-surface-brand' : 'bg-surface'} padding="300">
          <BlockStack gap="100">
            <Badge tone={isUser ? undefined : 'info'}>{isUser ? 'You' : 'Ally'}</Badge>
            <Text variant="bodyMd" as="p">
              {message.text}
            </Text>
          </BlockStack>
        </Card>
      </Box>
    </InlineStack>
  );
}

export default function Ally() {
  const { get, post } = useShopifyFetch();
  const navigate = useNavigate();

  // --- Chat state (primary interaction) ---
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isSending, setIsSending] = useState(false);
  const scrollAnchorRef = useRef<HTMLDivElement | null>(null);

  // Keep the newest message in view as the thread grows.
  useEffect(() => {
    scrollAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages, isSending]);

  const sendMessage = useCallback(
    async (rawText: string) => {
      const text = rawText.trim();
      if (!text || isSending) {
        return;
      }

      const userMessage: ChatMessage = { id: nextId(), role: 'user', text };
      setMessages((prev) => [...prev, userMessage]);
      setInput('');
      setIsSending(true);

      try {
        const data = await post<ChatResponse>('/api/ally/chat', {
          message: text,
          conversation_id: conversationId,
        });

        if (data.conversation_id) {
          setConversationId(data.conversation_id);
        }

        setMessages((prev) => [
          ...prev,
          {
            id: nextId(),
            role: 'ally',
            text: data.response || FRIENDLY_ERROR,
          },
        ]);
      } catch {
        // The backend proxy is best-effort and normally returns a friendly
        // message with a 200, but guard the transport layer too so a dropped
        // request never leaves the chat stuck.
        setMessages((prev) => [
          ...prev,
          { id: nextId(), role: 'ally', text: FRIENDLY_ERROR },
        ]);
      } finally {
        setIsSending(false);
      }
    },
    [conversationId, isSending, post]
  );

  const handleSend = useCallback(() => {
    void sendMessage(input);
  }, [input, sendMessage]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        void sendMessage(input);
      }
    },
    [input, sendMessage]
  );

  const hasConversation = messages.length > 0;

  // --- Insights state (panel below chat) ---
  const [days, setDays] = useState('30');
  const {
    data: insights,
    isLoading,
    error,
  } = useQuery<AllyInsights>({
    queryKey: ['ally', 'insights', days],
    queryFn: () => get(`/api/ally/insights?days=${days}`),
  });

  const connected = insights?.connected;
  const metrics = insights?.metrics;
  const doubleDown = insights?.recommendations?.double_down || [];
  const needsPromotion = insights?.recommendations?.needs_promotion || [];
  const topProducts = insights?.top_products || [];

  return (
    <Page
      title="Ally"
      subtitle="Your AI affiliate marketing assistant"
      titleMetadata={
        <Badge tone="info" icon={MagicIcon}>
          Ally
        </Badge>
      }
    >
      <Layout>
        {/* ---------- Chat (primary) ---------- */}
        <Layout.Section>
          <Card>
            <BlockStack gap="400">
              {/* Message thread */}
              <Box
                minHeight="360px"
                background="bg-surface-secondary"
                borderRadius="300"
                padding="400"
              >
                {!hasConversation ? (
                  <BlockStack gap="200" align="center">
                    <Text variant="headingMd" as="h2" alignment="center">
                      Hi! I&apos;m Ally, your AI marketing assistant
                    </Text>
                    <Text variant="bodyMd" tone="subdued" as="p" alignment="center">
                      Ask me about your products, affiliates, or how to grow conversions. Try a
                      suggestion below to get started.
                    </Text>
                  </BlockStack>
                ) : (
                  <BlockStack gap="300">
                    {messages.map((message) => (
                      <MessageBubble key={message.id} message={message} />
                    ))}
                    {isSending && (
                      <InlineStack gap="200" blockAlign="center">
                        <Spinner accessibilityLabel="Ally is thinking" size="small" />
                        <Text variant="bodySm" tone="subdued" as="span">
                          Ally is thinking…
                        </Text>
                      </InlineStack>
                    )}
                    <div ref={scrollAnchorRef} />
                  </BlockStack>
                )}
              </Box>

              {/* Prompt suggestions */}
              <InlineStack gap="200" wrap>
                {PROMPT_SUGGESTIONS.map((suggestion) => (
                  <Button
                    key={suggestion}
                    size="slim"
                    disabled={isSending}
                    onClick={() => void sendMessage(suggestion)}
                  >
                    {suggestion}
                  </Button>
                ))}
              </InlineStack>

              {/* Composer */}
              <InlineStack gap="200" blockAlign="center" wrap={false}>
                <Box width="100%">
                  <div onKeyDown={handleKeyDown}>
                    <TextField
                      label="Message Ally"
                      labelHidden
                      value={input}
                      onChange={setInput}
                      placeholder="Ask Ally anything…"
                      autoComplete="off"
                      disabled={isSending}
                      multiline={1}
                    />
                  </div>
                </Box>
                <Button
                  variant="primary"
                  onClick={handleSend}
                  loading={isSending}
                  disabled={!input.trim() || isSending}
                >
                  Send
                </Button>
              </InlineStack>
            </BlockStack>
          </Card>
        </Layout.Section>

        {/* ---------- Insights panel ---------- */}
        <Layout.Section>
          <Card>
            <InlineStack align="space-between" blockAlign="center" wrap>
              <InlineStack gap="200" blockAlign="center">
                <Icon source={MagicIcon} tone="base" />
                <Text as="h2" variant="headingMd">
                  What Ally sees in your affiliate performance
                </Text>
              </InlineStack>
              <Box width="200px">
                <Select
                  label="Time range"
                  labelHidden
                  options={[
                    { label: 'Last 7 days', value: '7' },
                    { label: 'Last 30 days', value: '30' },
                    { label: 'Last 90 days', value: '90' },
                  ]}
                  value={days}
                  onChange={setDays}
                />
              </Box>
            </InlineStack>
          </Card>
        </Layout.Section>

        {error ? (
          <Layout.Section>
            <Banner tone="critical">
              <p>Failed to load Ally insights. Please try again.</p>
            </Banner>
          </Layout.Section>
        ) : !isLoading && connected === false ? (
          <Layout.Section>
            <Banner
              tone="warning"
              title="Connect your Affilync account"
              action={{
                content: 'Go to Settings',
                onAction: () => navigate(withSearch('/settings')),
              }}
            >
              <p>
                Connect your Affilync brand account in Settings to see Ally&apos;s affiliate
                performance insights and promotion recommendations.
              </p>
            </Banner>
          </Layout.Section>
        ) : (
          <>
            {/* Metrics */}
            <Layout.Section>
              <InlineGrid columns={{ xs: 1, sm: 2, md: 4 }} gap="400">
                <MetricCard
                  label="Affiliate clicks"
                  value={(metrics?.clicks || 0).toLocaleString()}
                  loading={isLoading}
                />
                <MetricCard
                  label="Conversions"
                  value={(metrics?.conversions || 0).toLocaleString()}
                  loading={isLoading}
                />
                <MetricCard
                  label="Revenue"
                  value={`$${(metrics?.revenue || 0).toLocaleString()}`}
                  loading={isLoading}
                />
                <MetricCard
                  label="Conversion rate"
                  value={`${(metrics?.conversion_rate || 0).toFixed(1)}%`}
                  loading={isLoading}
                />
              </InlineGrid>
            </Layout.Section>

            {/* Recommendations */}
            <Layout.Section>
              <InlineGrid columns={{ xs: 1, md: 2 }} gap="400">
                {/* Double down */}
                <Card>
                  <BlockStack gap="300">
                    <InlineStack gap="200" blockAlign="center">
                      <Icon source={ChartVerticalFilledIcon} tone="success" />
                      <Text as="h3" variant="headingSm">
                        Double down on these
                      </Text>
                    </InlineStack>
                    {isLoading ? (
                      <SkeletonBodyText lines={4} />
                    ) : doubleDown.length > 0 ? (
                      <BlockStack gap="300">
                        {doubleDown.map((rec) => (
                          <BlockStack gap="100" key={rec.title}>
                            <InlineStack align="space-between" blockAlign="center" wrap={false}>
                              <Text as="span" variant="bodyMd" fontWeight="semibold" truncate>
                                {rec.title}
                              </Text>
                              <Badge tone="success">{`${(rec.conversions || 0).toLocaleString()} conv`}</Badge>
                            </InlineStack>
                            <Text as="span" variant="bodySm" tone="subdued">
                              {rec.reason}
                            </Text>
                          </BlockStack>
                        ))}
                      </BlockStack>
                    ) : (
                      <Text as="p" variant="bodyMd" tone="subdued">
                        No affiliate conversions yet in this period. Once products start converting,
                        Ally will flag the proven earners to scale.
                      </Text>
                    )}
                  </BlockStack>
                </Card>

                {/* Needs promotion */}
                <Card>
                  <BlockStack gap="300">
                    <InlineStack gap="200" blockAlign="center">
                      <Icon source={LightbulbIcon} tone="warning" />
                      <Text as="h3" variant="headingSm">
                        Give these a push
                      </Text>
                    </InlineStack>
                    {isLoading ? (
                      <SkeletonBodyText lines={4} />
                    ) : needsPromotion.length > 0 ? (
                      <List>
                        {needsPromotion.map((rec) => (
                          <List.Item key={rec.title}>
                            <Text as="span" variant="bodyMd" fontWeight="semibold">
                              {rec.title}
                            </Text>
                            <br />
                            <Text as="span" variant="bodySm" tone="subdued">
                              {rec.reason}
                            </Text>
                          </List.Item>
                        ))}
                      </List>
                    ) : (
                      <Text as="p" variant="bodyMd" tone="subdued">
                        Every synced product is already getting affiliate conversions — nice work!
                        Sync more products to surface new promotion opportunities.
                      </Text>
                    )}
                  </BlockStack>
                </Card>
              </InlineGrid>
            </Layout.Section>

            {/* Top products */}
            <Layout.Section>
              <Card>
                <BlockStack gap="400">
                  <Text as="h3" variant="headingSm">
                    Top products by affiliate revenue
                  </Text>
                  {isLoading ? (
                    <SkeletonBodyText lines={5} />
                  ) : topProducts.length > 0 ? (
                    <DataTable
                      columnContentTypes={['text', 'numeric', 'numeric']}
                      headings={['Product', 'Conversions', 'Revenue']}
                      rows={topProducts.map((p) => [
                        p.title,
                        p.conversions.toLocaleString(),
                        `$${p.revenue.toLocaleString()}`,
                      ])}
                    />
                  ) : (
                    <Text as="p" variant="bodyMd" tone="subdued">
                      No product performance data yet. Generate affiliate links and share them to
                      start tracking.
                    </Text>
                  )}
                </BlockStack>
              </Card>
            </Layout.Section>

            {/* Methodology note */}
            {insights?.recommendations?.note && (
              <Layout.Section>
                <Banner tone="info">
                  <p>{insights.recommendations.note}</p>
                </Banner>
              </Layout.Section>
            )}
          </>
        )}
      </Layout>
    </Page>
  );
}
